import moment from "moment";

// Pure helpers that turn the batch resolver response
//   members: [{ branchMemberId, days:[{date, slots, overrideMarkers}] }]
// into a month calendar grid of "which doctors work which day".
//
// Correctness note: a doctor counts as working a day iff that day has at least
// one resolved slot. Because the backend already applies overrides into `slots`,
// this automatically reflects leave (slots emptied -> doctor drops off the day)
// and extra shifts (slots added on a normally-off day -> doctor appears). The
// frontend never re-derives availability from RRule.

// -> Map<dateStr, Set<branchMemberId:Number>> for days that have resolved slots.
export function buildScheduleIndex(members) {
  const index = new Map();
  (members || []).forEach((m) => {
    if (!m || m.branchMemberId == null) return;
    const id = Number(m.branchMemberId);
    (m.days || []).forEach((d) => {
      if (!d || !Array.isArray(d.slots) || d.slots.length === 0) return;
      if (!index.has(d.date)) index.set(d.date, new Set());
      index.get(d.date).add(id);
    });
  });
  return index;
}

// Normalize a host doctor list into a lookup keyed by branchMemberId.
// Each doctor: { branchMemberId, name, color }.
function indexDoctors(doctors) {
  const map = new Map();
  (doctors || []).forEach((d) => {
    if (d == null || d.branchMemberId == null) return;
    map.set(Number(d.branchMemberId), {
      branchMemberId: Number(d.branchMemberId),
      name: d.name,
      color: d.color,
    });
  });
  return map;
}

export const MONTH_DOW_LABELS = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];

// Short label for a doctor chip: the first whitespace-delimited token of the
// full name. Full name is still shown in the chip title + roster panel.
export function firstName(name) {
  return (name || "").trim().split(/\s+/)[0] || "";
}

// Build the calendar grid for a month (month is 1..12), Sunday-first to match
// the existing CalendarGrid. Returns full weeks including adjacent-month padding.
// -> { cells: [{ date, dayNum, outside, doctors:[{branchMemberId,name,color}] }] }
// `doctors` per cell are sorted by name for stable rendering.
export function buildMonthGrid({ year, month, members = [], doctors = [] }) {
  const index = buildScheduleIndex(members);
  const byId = indexDoctors(doctors);

  const monthStart = moment(`${year}-${String(month).padStart(2, "0")}-01`);
  const monthEnd = monthStart.clone().endOf("month");
  const start = monthStart.clone().subtract(monthStart.day(), "day");
  const end = monthEnd.clone().add(6 - monthEnd.day(), "day");

  const cells = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    const date = cursor.format("YYYY-MM-DD");
    const ids = index.get(date) || new Set();
    const docs = [...ids]
      .map((id) => byId.get(id))
      .filter(Boolean)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    cells.push({
      date,
      dayNum: cursor.date(),
      outside: cursor.month() + 1 !== month,
      doctors: docs,
    });
    cursor.add(1, "day");
  }
  return { cells };
}

// branchMemberIds (Number) working a given date — used for day-click payloads
// and to reorder the doctor list in the manage view.
export function doctorIdsOnDate(members, date) {
  return [...(buildScheduleIndex(members).get(date) || new Set())];
}
